import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { DashboardShell } from "@/components/DashboardShell";
import { SUPER_ADMIN_NAV } from "@/lib/nav";
import { fetchSetting, saveSetting, type SystemSettings } from "@/lib/super-admin";

export const Route = createFileRoute("/_authenticated/super-admin/settings")({
  head: () => ({
    meta: [
      { title: "System settings · Landoo Super Admin" },
      {
        name: "description",
        content: "Platform name, support email, currency and maintenance mode.",
      },
    ],
  }),
  component: SettingsPage,
});

const DEFAULTS: SystemSettings = {
  platform_name: "Landoo",
  support_email: "support@landoo.app",
  default_currency: "INR",
  default_country: "IN",
  map_default_zoom: 12,
  maintenance_mode: false,
};

const input =
  "w-full rounded-lg border border-border bg-background px-3 py-2 text-xs outline-none focus:border-brand";

function SettingsPage() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["super-admin", "system-settings"],
    queryFn: () => fetchSetting<SystemSettings>("system_settings"),
  });
  const [settings, setSettings] = useState<SystemSettings>(DEFAULTS);
  useEffect(() => {
    if (data) setSettings({ ...DEFAULTS, ...data });
  }, [data]);

  const save = async () => {
    try {
      await saveSetting("system_settings", settings);
      toast.success("System settings saved");
      qc.invalidateQueries({ queryKey: ["super-admin", "system-settings"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not save");
    }
  };

  const patch = <K extends keyof SystemSettings>(k: K, v: SystemSettings[K]) =>
    setSettings((s) => ({ ...s, [k]: v }));

  return (
    <DashboardShell title="System settings" role="Super Admin" items={SUPER_ADMIN_NAV}>
      <div className="grid gap-3 sm:grid-cols-2">
        <F label="Platform name">
          <input
            className={input}
            value={settings.platform_name}
            onChange={(e) => patch("platform_name", e.target.value)}
          />
        </F>
        <F label="Support email">
          <input
            className={input}
            type="email"
            value={settings.support_email}
            onChange={(e) => patch("support_email", e.target.value)}
          />
        </F>
        <F label="Default currency">
          <input
            className={input}
            value={settings.default_currency}
            onChange={(e) => patch("default_currency", e.target.value)}
          />
        </F>
        <F label="Default country">
          <input
            className={input}
            value={settings.default_country}
            onChange={(e) => patch("default_country", e.target.value)}
          />
        </F>
        <F label="Map default zoom">
          <input
            className={input}
            type="number"
            min={4}
            max={20}
            value={settings.map_default_zoom}
            onChange={(e) => patch("map_default_zoom", Number(e.target.value) || 12)}
          />
        </F>
        <label className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4 sm:col-span-2">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4"
            checked={settings.maintenance_mode}
            onChange={(e) => patch("maintenance_mode", e.target.checked)}
          />
          <div>
            <div className="text-sm font-semibold text-brown-dark">Maintenance mode</div>
            <p className="text-xs text-muted-foreground">
              When enabled, the app shows a maintenance message and new sign-ups are paused.
              Existing data is preserved.
            </p>
          </div>
        </label>
      </div>
      <button
        onClick={save}
        className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-coral px-4 py-2 text-xs font-semibold text-white shadow-soft"
      >
        <Save className="h-4 w-4" /> Save settings
      </button>
    </DashboardShell>
  );
}

function F({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      {children}
    </label>
  );
}
